import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import {
  getIcon,
  formatPlatformName,
  DELETE_MODE,
  CONFIGURABLE_INTEGRATIONS,
} from "../../constants/constants";
import { Icons } from "../../constants/icon-library";
import { getIntegration } from "../../features/meeting-integration/meetingIntegrationSlice";
import {
  fetchIntegrationSettingById,
  upsertIntegrationSetting,
  deleteIntegrationSetting,
  refreshIntegrationSetting,
} from "../../features/integration-setting/integrationSettingSlice";
import LoadingComponent from "../../components/common/LoadingComponent";
import { toast } from "react-toastify";
import { INTEGRATION_ENUM } from "../../constants/constants";

function IntegrationDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [deleteMode, setDeleteMode] = useState(DELETE_MODE.SETTING_ONLY);
  const [isEditingApiKey, setIsEditingApiKey] = useState(false);
  const [isEditingSubDomain, setIsEditingSubDomain] = useState(false);
  const { selectedIntegration, loading: integrationLoading } = useSelector(
    (state) => state.meetingIntegrations,
  );
  const { setting, loading: settingLoading } = useSelector(
    (state) => state.integrationSetting,
  );
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");

  useEffect(() => {
    if (!id) return;

    dispatch(
      fetchIntegrationSettingById({
        settingUuid: id,
      }),
    );
  }, [id, dispatch]);

  const integrationId = useMemo(() => {
    if (!setting?.integrationId) return null;

    return typeof setting.integrationId === "object"
      ? setting.integrationId?.uuid
      : setting.integrationId;
  }, [setting]);

  useEffect(() => {
    if (
      integrationId &&
      (!selectedIntegration || selectedIntegration?.uuid !== integrationId)
    ) {
      dispatch(getIntegration(integrationId));
    }
  }, [integrationId, selectedIntegration, dispatch]);

  const integrationName = selectedIntegration?.name?.toLowerCase();
  const iconSrc = useMemo(() => getIcon(integrationName), [integrationName]);

  const fetchUpdatedSetting = useCallback(async () => {
    if (!setting?.uuid) return;

    await dispatch(
      fetchIntegrationSettingById({
        settingUuid: setting.uuid,
      }),
    ).unwrap();
  }, [dispatch, setting?.uuid]);

  const handleRefreshIntegration = useCallback(async () => {
    try {
      await dispatch(
        refreshIntegrationSetting({
          settingId: setting?.uuid,
        }),
      ).unwrap();

      await fetchUpdatedSetting();
    } catch (error) {
      console.error("Refresh failed:", error);
    }
  }, [dispatch, setting?.uuid, fetchUpdatedSetting]);

  const handleDelete = useCallback(async () => {
    try {
      await dispatch(
        deleteIntegrationSetting({
          uuid: setting?.uuid,
          deleteMode,
        }),
      ).unwrap();

      navigate("/integrations");
    } catch (error) {
      console.error(error);
    }
  }, [dispatch, setting?.uuid, deleteMode, navigate]);

  const formik = useFormik({
    initialValues: {
      apiKey: "",
      subDomain: "",
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors = {};
      if (!values.apiKey?.trim()) {
        errors.apiKey = "API key is required";
      }
      if (
        integrationName === INTEGRATION_ENUM.FELLOW_AI &&
        !values.subDomain?.trim()
      ) {
        errors.subDomain = "Sub Domain is required";
      }
      return errors;
    },
  });

  const handleConfiguration = async (setting) => {
    navigate(`/integrations/configuration/${setting?.uuid}`);
  };
  const isLoading = integrationLoading || settingLoading;

  const handleApiKeySave = async () => {
    if (!formik.values.apiKey?.trim()) {
      await Promise.all([
        formik.setFieldTouched("apiKey", true, false),
        formik.setFieldError("apiKey", "API key is required"),
      ]);
      return;
    }

    formik.setFieldError("apiKey", undefined);

    try {
      await dispatch(
        upsertIntegrationSetting({
          uuid: setting?.uuid,
          integrationId,
          accessToken: formik.values.apiKey,
          isInstallationCompleted: true,
        }),
      ).unwrap();

      setIsEditingApiKey(false);
      await fetchUpdatedSetting();
      await dispatch(
        refreshIntegrationSetting({ settingId: setting?.uuid }),
      ).unwrap();

      toast.success("API key updated successfully");
    } catch (error) {
      console.error(error);
      toast.error(error?.message || "Failed to update API key");
    }
  };

  const handleSubDomainSave = async () => {
    if (!formik.values.subDomain?.trim()) {
      await Promise.all([
        formik.setFieldTouched("subDomain", true, false),
        formik.setFieldError("subDomain", "Sub Domain is required"),
      ]);
      return;
    }

    formik.setFieldError("subDomain", undefined);
    try {
      await dispatch(
        upsertIntegrationSetting({
          uuid: setting?.uuid,
          integrationId,
          subDomain: formik.values.subDomain,
          isInstallationCompleted: true,
        }),
      ).unwrap();

      setIsEditingSubDomain(false);
      await fetchUpdatedSetting();
      await dispatch(
        refreshIntegrationSetting({ settingId: setting?.uuid }),
      ).unwrap();

      toast.success("Sub domain updated successfully");
    } catch (error) {
      console.error(error);
      toast.error(error?.message || "Failed to update sub domain");
    }
  };

  return (
    <div className="container my-6">
      <div
        className="card border-0 shadow-lg"
        style={{
          marginTop: "60px",
        }}
      >
        <div className="card-body p-4 p-md-5">
          <div className="d-flex justify-content-between align-items-start mb-5">
            <div className="d-flex align-items-center">
              <div
                className="rounded-circle bg-gradient d-flex justify-content-center align-items-center me-3 flex-shrink-0"
                style={{
                  width: "60px",
                  height: "60px",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  boxShadow: "0 4px 15px rgba(102,126,234,0.4)",
                }}
              >
                {iconSrc && (
                  <img
                    src={iconSrc}
                    alt={integrationName}
                    style={{
                      width: "36px",
                      height: "36px",
                    }}
                  />
                )}
              </div>

              <div>
                <div className="flex-grow-1">
                  {!isEditingName ? (
                    <div className="d-flex align-items-center">
                      <h3 className="mb-1 fw-bold text-truncate">
                        {formatPlatformName(
                          setting?.integrationName || integrationName,
                        )}
                      </h3>

                      <button
                        className="btn btn-sm flex-shrink-0"
                        onClick={() => {
                          setIsEditingName(true);
                          setEditedName(
                            setting?.integrationName || integrationName || "",
                          );
                        }}
                        aria-label="Edit integration name"
                      >
                        <i className={`wb ${Icons("edit")}`} />
                      </button>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center gap-2 flex-grow-1">
                      <input
                        type="text"
                        className="form-control bg-opacity-25 border-secondary"
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                      />

                      <button
                        className="btn btn-sm btn-success"
                        onClick={async () => {
                          await dispatch(
                            upsertIntegrationSetting({
                              uuid: setting?.uuid,
                              integrationName: editedName,
                            }),
                          );

                          setIsEditingName(false);
                          fetchUpdatedSetting();
                        }}
                      >
                        <i className={`wb ${Icons("correct")}`} />
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => setIsEditingName(false)}
                      >
                        <i className={`wb ${Icons("cancel")}`} />
                      </button>
                    </div>
                  )}

                  <p
                    className="text-muted mb-0"
                    style={{
                      fontSize: "0.9rem",
                    }}
                  >
                    Manage your integration settings and imported meetings
                  </p>
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center gap-1">
              {CONFIGURABLE_INTEGRATIONS?.includes(selectedIntegration?.name) &&
              selectedIntegration?.name !== INTEGRATION_ENUM.GMAIL ? (
                <button
                  type="button"
                  className="btn btn-sm btn-setup d-flex align-items-center gap-2"
                  onClick={() => handleConfiguration(setting)}
                >
                  <i className={`wb ${Icons("constraints")}`} />
                  Settings
                </button>
              ) : selectedIntegration?.name !== INTEGRATION_ENUM.GMAIL ? (
                <button
                  type="button"
                  className="btn btn-sm btn-setup d-flex align-items-center gap-2"
                  disabled={settingLoading}
                  onClick={handleRefreshIntegration}
                >
                  <i className={`wb ${Icons("load")}`} />
                  {settingLoading ? "Refreshing..." : "Refresh Data"}
                </button>
              ) : null}
              <button
                type="button"
                className="btn btn-sm btn-primary d-flex align-items-center gap-2"
                onClick={() => navigate("/meetings")}
              >
                <i className={`wb webconnect-lightbulb`} />
                View Meeting
              </button>
            </div>
          </div>

          {isLoading ? (
            <LoadingComponent loading={true} size={15} />
          ) : (
            <>
              {!selectedIntegration?.isOauthRequired && (
                <div
                  className="card border shadow-sm mb-4"
                  style={{
                    borderRadius: "14px",
                  }}
                >
                  <div className="card-body p-4">
                    <div className="d-flex justify-content-between align-items-start mb-4">
                      <div>
                        <h5 className="fw-bold mb-1">API Key Access</h5>

                        <p
                          className="text-muted mb-0"
                          style={{
                            fontSize: "0.9rem",
                          }}
                        >
                          View and update your API key{" "}
                          {integrationName === INTEGRATION_ENUM.FELLOW_AI
                            ? " and Sub Domain"
                            : ""}
                        </p>
                      </div>

                      <span className="badge bg-success-subtle text-success px-3 py-2">
                        Connected
                      </span>
                    </div>

                    <form onSubmit={formik.handleSubmit}>
                      <label
                        className="text-muted d-block mb-2"
                        style={{
                          fontSize: "0.85rem",
                        }}
                      >
                        API Key
                      </label>

                      <div
                        className="d-flex align-items-center justify-content-between px-3"
                        style={{
                          minHeight: "52px",
                          borderRadius: "10px",
                          border:
                            formik.touched.apiKey && formik.errors.apiKey
                              ? "1px solid rgba(241,21,21,0.47)"
                              : "1px solid rgba(255,255,255,0.15)",
                          background: "rgba(255,255,255,0.03)",
                        }}
                      >
                        <div className="d-flex align-items-center flex-grow-1">
                          <i
                            className={`wb ${
                              Icons("key") || Icons("edit")
                            } me-2 text-muted`}
                          />

                          {isEditingApiKey ? (
                            <input
                              type="text"
                              name="apiKey"
                              className="form-control border-0 bg-transparent shadow-none px-2"
                              placeholder="Enter API Key"
                              value={formik.values.apiKey}
                              onChange={(e) => {
                                formik.handleChange(e);
                              }}
                              onBlur={formik.handleBlur}
                            />
                          ) : (
                            <span className="px-2">
                              {setting?.maskedAccessToken || "-"}
                            </span>
                          )}
                        </div>

                        <div className="d-flex align-items-center gap-3">
                          {!isEditingApiKey && (
                            <>
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => setIsEditingApiKey(true)}
                              >
                                Edit
                              </button>
                            </>
                          )}

                          {isEditingApiKey && (
                            <div className="d-flex align-items-center gap-2">
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                disabled={settingLoading}
                                onClick={handleApiKeySave}
                              >
                                Save
                              </button>

                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => {
                                  setIsEditingApiKey(false);

                                  formik.resetForm();
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {formik.touched.apiKey && formik.errors.apiKey && (
                        <div
                          className="text-danger mt-2"
                          style={{
                            fontSize: "0.8rem",
                          }}
                        >
                          {formik.errors.apiKey}
                        </div>
                      )}

                      {integrationName === INTEGRATION_ENUM.FELLOW_AI && (
                        <>
                          <label
                            className="text-muted d-block mb-2 mt-4"
                            style={{
                              fontSize: "0.85rem",
                            }}
                          >
                            Sub Domain
                          </label>

                          <div
                            className="d-flex align-items-center justify-content-between px-3"
                            style={{
                              minHeight: "52px",
                              borderRadius: "10px",
                              border:
                                formik.touched.subDomain &&
                                formik.errors.subDomain
                                  ? "1px solid rgba(241,21,21,0.47)"
                                  : "1px solid rgba(255,255,255,0.15)",
                              background: "rgba(255,255,255,0.03)",
                            }}
                          >
                            <div className="d-flex align-items-center flex-grow-1">
                              <i className="wb webconnect-globe me-2 text-muted" />

                              {isEditingSubDomain ? (
                                <>
                                  <input
                                    type="text"
                                    name="subDomain"
                                    className="form-control border-0 bg-transparent shadow-none px-2"
                                    placeholder="Enter Sub Domain"
                                    value={formik.values.subDomain}
                                    onChange={(e) => {
                                      formik.handleChange(e);
                                    }}
                                    onBlur={formik.handleBlur}
                                  />
                                  <span
                                    className="text-muted mr-2"
                                    style={{
                                      fontSize: "0.85rem",
                                      whiteSpace: "nowrap",
                                      marginRight: "2rem",
                                    }}
                                  >
                                    .fellow.app
                                  </span>
                                </>
                              ) : (
                                <span className="px-2">
                                  {setting?.subDomain || "-"}
                                </span>
                              )}
                            </div>

                            <div className="d-flex align-items-center gap-3">
                              {!isEditingSubDomain && (
                                <>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    onClick={() => setIsEditingSubDomain(true)}
                                  >
                                    Edit
                                  </button>
                                </>
                              )}

                              {isEditingSubDomain && (
                                <div className="d-flex align-items-center gap-2">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    disabled={settingLoading}
                                    onClick={handleSubDomainSave}
                                  >
                                    Save
                                  </button>

                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => {
                                      setIsEditingSubDomain(false);

                                      formik.resetForm();
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>

                          {formik.touched.subDomain &&
                            formik.errors.subDomain && (
                              <div
                                className="text-danger mt-2"
                                style={{
                                  fontSize: "0.8rem",
                                }}
                              >
                                {formik.errors.subDomain}
                              </div>
                            )}
                        </>
                      )}
                    </form>
                  </div>
                </div>
              )}

              <div
                className="card border shadow-sm"
                style={{
                  borderRadius: "14px",
                }}
              >
                <div className="card-body p-4">
                  <div className="mb-4">
                    <h5 className="fw-bold mb-1">Manage Your Integration</h5>

                    <p
                      className="text-muted mb-0"
                      style={{
                        fontSize: "0.9rem",
                      }}
                    >
                      Delete your integration settings and meetings
                    </p>
                  </div>
                  <div className="d-flex flex-column gap-3">
                    <label className="p-3 rounded border d-flex gap-3">
                      <input
                        type="radio"
                        value="settingOnly"
                        checked={deleteMode === DELETE_MODE.SETTING_ONLY}
                        onChange={(e) => setDeleteMode(e.target.value)}
                      />

                      <div>
                        <div className="fw-semibold">
                          Delete Integration Only
                        </div>

                        <div className="text-muted small">
                          Meetings remain in workspace
                        </div>
                      </div>
                    </label>

                    <label className="p-3 rounded border d-flex gap-3">
                      <input
                        type="radio"
                        value="allMeetings"
                        checked={deleteMode === DELETE_MODE.ALL_MEETINGS}
                        onChange={(e) => setDeleteMode(e.target.value)}
                      />

                      <div>
                        <div className="fw-semibold">
                          Delete Integration + All Conversations
                        </div>

                        <div className="text-muted small">
                          Remove all imported meetings
                        </div>
                      </div>
                    </label>
                  </div>

                  <div className="mt-4 d-flex justify-content-end">
                    <button
                      className="btn btn-danger"
                      onClick={handleDelete}
                      disabled={settingLoading}
                    >
                      Delete Integration
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => navigate("/integrations")}
                >
                  Back
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default IntegrationDetails;
